<!-- @catalog Microphone button that dictates spoken text into a field. -->
<template>
  <c-button-icon
    v-if="isSupported === true"
    flat
    size="sm"
    :icon="icon"
    :disable="disable === true || isTranscribing === true"
    :tooltip="tooltip"
    :aria-label="label"
    :color="buttonColor"
    @click="toggle"
    @mouseenter="isHighlighted = true"
    @mouseleave="isHighlighted = false"
    @focusin="isHighlighted = true"
    @focusout="isHighlighted = false"
  />

  <!-- Teleported: the field it belongs to scrolls away, and a teacher who
       cannot see the microphone has no way to tell the recording is still
       running. -->
  <Teleport to="body">
    <div
      v-if="isRecording === true"
      class="voice-recording"
      role="status"
      aria-live="polite"
    >
      <span class="voice-recording__dot"></span>

      <span class="voice-recording__title">
        {{ t("pages.voiceInput.recording") }}
      </span>

      <span v-if="voiceLabel" class="voice-recording__label">{{
        voiceLabel
      }}</span>

      <span class="voice-recording__clock">
        {{ toElapsedClock(elapsedSeconds) }}
      </span>

      <span class="voice-recording__hint">
        {{ t("pages.voiceInput.stopHint") }}
      </span>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useEventListener, useIntervalFn } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import CButtonIcon from "shared/components/wrappers/button/CButtonIcon.vue";
import { useVoiceInput } from "shared/composables/useVoiceInput";
import { toElapsedClock } from "shared/utils";
import { useNotifyStore } from "store/modules/notifyStore";

const props = withDefaults(
  defineProps<{
    disable?: boolean;
    /** Whose feedback is being dictated, shown in the recording indicator. */
    voiceLabel?: string;
  }>(),
  {
    disable: false,
    voiceLabel: "",
  },
);

const emit = defineEmits<{
  /**
   * The running transcript of the current recording. While dictating live this
   * fires many times with `isFinal` false; the last call of a recording is the
   * one worth saving.
   */
  transcript: [text: string, isFinal: boolean];
}>();

const { t } = useI18n();
const { setNotifyError } = useNotifyStore();

const { isSupported, isRecording, isTranscribing, error, toggle } =
  useVoiceInput((text, isFinal) => emit("transcript", text, isFinal));

// Counted from a timestamp rather than by adding a second per tick: a
// backgrounded tab throttles the interval, and a counter that drifts behind the
// recording is worse than no counter.
const startedAt = ref(0);
const elapsedSeconds = ref(0);

const { pause, resume } = useIntervalFn(
  () => {
    elapsedSeconds.value = Math.floor((Date.now() - startedAt.value) / 1000);
  },
  1000,
  { immediate: false },
);

watch(isRecording, (recording) => {
  if (recording === true) {
    startedAt.value = Date.now();
    elapsedSeconds.value = 0;
    resume();

    return;
  }

  pause();
});

// The microphone that is recording is often scrolled out of view, and hunting
// for it costs a teacher more than the sentence they were dictating. Only the
// field that holds the microphone listens (one at a time — the composable hands
// it over), and swallowing the key is the point: it stops the recording instead
// of landing as a space in the text.
useEventListener(document, "keydown", (event: KeyboardEvent) => {
  if (isRecording.value === false || event.code !== "Space") {
    return;
  }

  event.preventDefault();
  void toggle();
});

const icon = computed(() => {
  if (isTranscribing.value === true) {
    return "fa-solid fa-spinner fa-spin";
  }

  if (isRecording.value === true) {
    return "fa-solid fa-stop";
  }

  return "fa-solid fa-microphone";
});

const label = computed(() => {
  if (isTranscribing.value === true) {
    return t("pages.voiceInput.transcribing");
  }

  if (isRecording.value === true) {
    return t("pages.voiceInput.stop");
  }

  return t("pages.voiceInput.start");
});

// The microphone is the one control in a feedback field whose purpose is not
// obvious: nothing tells a teacher whether it records an audio note or types
// for them. Replaces the browser's own `title` — both at once shows two
// tooltips. Once it is running, how to stop it is what matters.
const tooltip = computed(() =>
  isRecording.value === true ? label.value : t("pages.voiceInput.tooltip"),
);

// Hover and focus are tracked here rather than in CSS: Quasar's own
// `.text-{color}` is `!important` and CButton hardcodes `color="green"` ahead
// of its `v-bind="filteredAttrs"`, so only the `color` attribute reliably wins.
const isHighlighted = ref(false);

// At rest the microphone steps back — it is one control among several in a
// feedback field, and a full-strength icon there reads as a required action.
// It comes forward on hover or focus, and turns red while it is recording.
const buttonColor = computed(() => {
  if (isRecording.value === true) {
    return "red";
  }

  if (isHighlighted.value === true || isTranscribing.value === true) {
    return "grey-800";
  }

  return "grey-500";
});

// A denied microphone or a failed transcription is invisible otherwise: the
// field simply stays empty and the teacher has no idea why.
watch(error, (errorKey) => {
  if (errorKey === null) {
    return;
  }

  setNotifyError({
    error: new Error(t(`pages.voiceInput.${errorKey}`)),
    fallbackMessage: t("pages.voiceInput.transcriptionFailed"),
  });
});
</script>

<style lang="scss" scoped>
.voice-recording {
  @apply tw-fixed tw-bottom-6 tw-left-1/2 tw-z-[9999] tw-flex -tw-translate-x-1/2 tw-items-center tw-gap-3 tw-rounded-full tw-bg-gray-900 tw-px-5 tw-py-3 tw-text-white tw-shadow-lg;

  &__dot {
    @apply tw-h-2.5 tw-w-2.5 tw-animate-pulse tw-rounded-full tw-bg-red-500;
  }

  &__title {
    @apply tw-text-sm tw-font-semibold;
  }

  &__label {
    @apply tw-text-sm;
  }

  &__clock {
    @apply tw-text-sm tw-tabular-nums tw-text-gray-300;
  }

  &__hint {
    @apply tw-rounded-full tw-bg-white/10 tw-px-2 tw-py-0.5 tw-text-xs tw-text-gray-300;
  }
}
</style>
